import { useDisclosure } from "@mantine/hooks";
import { useState, useRef, useCallback, FormEvent, useEffect } from "react";
import { useForm } from "react-hook-form";
import { DashboardPage } from "../../../components/DashboardPage";
import { DrawerVariables } from "../../../components/Drawer/DrawerVariables";
import { IGenericFormRefProps, GenericForm } from "../../../components/GenericForm";
import { ContentOrLoading } from "../../../components/Loadings/ContentOrLoading";
import { ModalLoading } from "../../../components/Loadings/ModalLoading";
import { useApi } from "../../../hooks/api";
import { useGlobal } from "../../../hooks/global";
import { MaskTypes, fieldTypes, IGroupProps, IFieldProps } from "../../../interfaces";
import { maskFunctions } from "../../../services/maskServices";
import { background, secondary } from "../../../theme";
import { Column } from "../Dashboard/style";
import { H4 } from "../Subscription/style";
import { enabledDisabledOptions } from "./options/enabledDisabledOptions";
import { ContainerForm, Form, ContainerButtons } from "./style";
import { Line } from "../../../style";
import Button from "../../../components/Buttons/Button";

interface IConstantsMapper {
  label: string,
  description: string,
  type?: MaskTypes,
  inputType?: fieldTypes,
  required?: boolean,
  options?: {
    label: string;
    value: number | string;
  }[],
  maxChars?: number;
  disabled?: boolean;
}

interface TimezoneOption {
  value: string;
  label: string;
}

const timezoneOptions: TimezoneOption[] = [
  { value: '-720', label: 'UTC-12:00 (Linha Internacional de Data)' },
  { value: '-600', label: 'UTC-10:00 (Honolulu)' },
  { value: '-480', label: 'UTC-08:00 (Los Angeles)' },
  { value: '-420', label: 'UTC-07:00 (Denver)' },
  { value: '-360', label: 'UTC-06:00 (Chicago)' },
  { value: '-300', label: 'UTC-05:00 (Nova York)' },
  { value: '-240', label: 'UTC-04:00 (Manaus)' },
  { value: '-180', label: 'UTC-03:00 (São Paulo, Brasília)' },
  { value: '-120', label: 'UTC-02:00 (Fernando de Noronha)' },
  { value: '-60', label: 'UTC-01:00 (Açores)' },
  { value: '0', label: 'UTC±00:00 (Londres)' },
  { value: '60', label: 'UTC+01:00 (Paris)' },
  { value: '120', label: 'UTC+02:00 (Cairo)' },
  { value: '180', label: 'UTC+03:00 (Moscou)' },
  { value: '210', label: 'UTC+03:30 (Teerã)' },
  { value: '240', label: 'UTC+04:00 (Dubai)' },
  { value: '300', label: 'UTC+05:00 (Paquistão)' },
  { value: '360', label: 'UTC+06:00 (Almaty)' },
  { value: '480', label: 'UTC+08:00 (Pequim, Hong Kong)' },
  { value: '540', label: 'UTC+09:00 (Tóquio)' },
  { value: '600', label: 'UTC+10:00 (Sydney)' },
  { value: '660', label: 'UTC+11:00 (Austrália: Ilha Norfolk)' },
  { value: '720', label: 'UTC+12:00 (Ilhas Fiji)' },
];


export const GeneralSettingsDefault: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [loadingSave, setLoadingSave] = useState(false);

  const { api, user } = useApi();
  const { notify, theme } = useGlobal();

  const form_ref = useRef<IGenericFormRefProps>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const [opened, { open, close }] = useDisclosure(false);

  const CONSTANTS_MAPPER: { [key: string]: IConstantsMapper } = {
    CHECKIN_TEXT_FISIC_KEY: {
      label: "Mensagem de sucesso sem exibição de senha",
      description: "Mensagem de sucesso do web checkin para chave comum ou quando a exibição da senha estiver desabilitada",
      inputType: 'textarea',
      maxChars: 700
    },
    CHECKIN_TEXT_PASSWORD_KEY: {
      label: "Mensagem de sucesso com exibição de senha",
      description: "Mensagem de sucesso do web checkin quando a senha for exibida, seja ela online ou offline",
      inputType: 'textarea',
      maxChars: 700
    },
  }

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
    setError,
    setValue,
    getValues,
    trigger,
  } = useForm({
    defaultValues: ({} as any),
    mode: "all",
  });

  const _form = watch();

  const searchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/constants?is_default=${true}`);

      for (const key of Object.keys(data)) {
        const { type, inputType } = CONSTANTS_MAPPER?.[key] ?? {}

        if (!type && !inputType) continue

        let value = data[key]

        if (type === 'porcen') value = maskFunctions.porcen.mask(value * 100) + ' %'

        if (inputType === 'select-fixed') {
          if (type === 'text') {
            value = timezoneOptions.find((option) => option.value === value);
          } else {
            value = enabledDisabledOptions.find((option) => option.value === value);
          }
        }

        setValue(key, value)
      }
    } catch (error) {
      console.error(error);
      notify("Erro ao buscar as configurações", 'error');
    }
    setLoading(false);
  }, [api]);

  const onSubmit = useCallback(async () => {
    setLoadingSave(true);

    try {
      const form = form_ref.current?.getForm();

      for (const key of Object.keys(form ?? {})) {
        const { type } = CONSTANTS_MAPPER?.[key] ?? {}
        if (!type) continue
        if (type === 'text') {
          form[key] = form[key]?.trim()?.toLowerCase();
          continue;
        }
        let value = Number(form[key])
        if (type === 'porcen') value = value / 100
        form[key] = value
      }

      await api.put("root/general-constants-default", form);

      notify('Configurações atualizadas com sucesso!', 'success');

    } catch (err: any) {
      console.error(err);
      const error = err.response ? err.response?.data : "SERVER ERROR";
      if (error.field)
        setError(error.field, { type: "manual", message: error.detail });
      else notify("ERRO INTERNO DO SISTEMA", "error");
    }

    setLoadingSave(false);
  }, [form_ref, _form, api, searchSettings]);

  const handleStopPropagation = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e?.stopPropagation();
      handleSubmit(onSubmit)(e);
    },
    [handleSubmit, onSubmit, form_ref, _form, api]
  );

  const groups: IGroupProps[] = [
    {
      name: "",
      label: "",
      fields: [
        Object.entries(CONSTANTS_MAPPER)
          .filter(([key]) => key !== 'CHECKIN_TEXT_FISIC_KEY' && key !== 'CHECKIN_TEXT_PASSWORD_KEY')
          .map(([key, { label, description, type, inputType, options, required = true, maxChars, disabled }]) => {
            return {
              name: key,
              label,
              type: inputType || "input",
              mask: type,
              required: required,
              tooltip: description,
              options: options,
              getIsDisabled: (form) => disabled,
              maxChars
            } as IFieldProps
          }),

        Object.entries(CONSTANTS_MAPPER)
          .filter(([key]) => key === 'CHECKIN_TEXT_FISIC_KEY' || key === 'CHECKIN_TEXT_PASSWORD_KEY')
          .map(([key, { label, description, type, inputType, options, required = true, maxChars }]) => ({
            name: key,
            label,
            type: inputType || "input",
            mask: type,
            required: required,
            tooltip: description,
            options: options,
            maxChars
          }) as IFieldProps),
      ],
    },
  ];

  useEffect(() => { searchSettings() }, [])

  return (
    <DashboardPage>
      <Column style={{ marginBottom: 16 }}>
        <Line>
          <H4>Configurações Gerais</H4>
        </Line>
      </Column>
      <ContentOrLoading loading={loading} theme={theme}>
        <Column style={{ flex: 1, paddingTop: 20 }}>
          <ContainerForm open>
            <Form onSubmit={handleStopPropagation}>
              <GenericForm
                ref={form_ref}
                groups={groups}
                _form={_form}
                control={control}
                trigger={trigger}
                errors={errors}
                setValue={setValue}
                getValues={getValues}
                register={register}
                disabled={loadingSave || !user.user_type.permissions.includes('ROOT')}
              />
              <button className="display-none" type="submit" ref={submitButtonRef} />
            </Form>
          </ContainerForm>
        </Column>
        {user.user_type.permissions.includes('ROOT') && (
          <ContainerButtons style={{ marginBottom: -10, marginTop: 40 }} theme={theme}>
            <Button
              onClick={open}
              background={background[theme]}
              borderColor={secondary[theme]}
              color={secondary[theme]}
              style={{ padding: 8, maxWidth: 150, fontSize: 14 }}
              children={"Variáveis"}
            />
            <Button
              onClick={() => submitButtonRef.current?.click()}
              background={secondary[theme]}
              color={"#FFF"}
              style={{ padding: 8, maxWidth: 150, fontSize: 14 }}
              children={"Salvar"}
            />
          </ContainerButtons>
        )}
        <ModalLoading loading={loadingSave} theme={theme} />
      </ContentOrLoading>

      <DrawerVariables opened={opened} close={close} />
    </DashboardPage>
  );
};
